import { Message, useChat } from '@ai-sdk/react';
import { BitteToolResult, ChatRequestBody } from '@bitte-ai/types';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ReactNode, useEffect, useMemo } from 'react';
import { Hex } from 'viem';
import { AssistantsMode } from '../lib/constants';
import { executeLocalToolCall } from '../lib/local-agent';
import { useAccount } from './wallet.state';

// Define the config type
interface ChatConfig {
  chatId: string;
  apiUrl: string;
  apiKey: string;
  agentId: string;
  initialMessages: Message[];
  localAgent: any;
  mcpServerUrl: string;
  format?: 'markdown' | 'plaintext';
}

// Chat configuration
export const chatConfigAtom = atom<ChatConfig>({
  chatId: '',
  apiUrl: '',
  apiKey: '',
  agentId: '',
  initialMessages: [] as Message[],
  localAgent: undefined as any,
  mcpServerUrl: '',
  format: 'plaintext',
});

export const chatStateAtom = atom<ReturnType<typeof useChat> | null>(null);

export const chatIdAtom = atom((get) => get(chatConfigAtom).chatId);

export const useChatState = () => {
  const chatState = useAtomValue(chatStateAtom);

  if (!chatState) {
    return {
      messages: [],
      isLoading: true,
      status: 'ready' as const,
      input: '',
      handleInputChange: () => {},
      handleSubmit: () => {},
      reload: () => {},
      addToolResult: () => {},
      append: () => Promise.resolve(null),
      error: null,
    };
  }

  return chatState;
};

export function ChatInitializer({ children }: { children: ReactNode }) {
  const {
    accountId,
    evmAddress,
    chainId,
    suiAddress,
    nearWalletId,
    cardanoAddress,
    solanaWallet,
  } = useAccount();
  const [config] = useAtom(chatConfigAtom);
  const setChatState = useSetAtom(chatStateAtom);

  // Ensure API URL is absolute to prevent routing issues
  const absoluteApiUrl = useMemo(() => {
    if (!config.apiUrl) {
      console.warn('Chat API URL is not configured');
      return undefined; // Return undefined instead of empty string
    }
    if (config.apiUrl.startsWith('http')) return config.apiUrl;
    if (typeof window !== 'undefined') {
      return `${window.location.origin}${config.apiUrl}`;
    }
    return config.apiUrl;
  }, [config.apiUrl]);

  const chatState = useChat({
    maxSteps: 7,
    id: config.chatId,
    api: absoluteApiUrl,
    experimental_throttle: 50, // Reduced from 300ms to 50ms for smoother streaming
    onToolCall: async ({ toolCall }): Promise<BitteToolResult | undefined> => {
      if (!config.localAgent) return undefined;

      try {
        return await executeLocalToolCall({
          localAgent: config.localAgent,
          toolCall,
          metadata: {
            accountId,
            evmAddress,
            chainId,
            suiAddress,
            cardanoAddress,
            solanaAddress: solanaWallet?.publicKey?.toBase58(),
          },
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        console.error('Error executing tool call:', errorMessage);
        return { error: errorMessage };
      }
    },
    onError: (e) => {
      console.error(e);
    },
    sendExtraMessageFields: true,
    initialMessages: config.initialMessages,
    headers: {
      Authorization: `Bearer ${config.apiKey}`,
    },
    body: {
      id: config.chatId,
      config: {
        mode: AssistantsMode.DEBUG,
        agentId: config.agentId,
        mcpServerUrl: config.mcpServerUrl,
        format: config.format,
      },
      nearWalletId,
      accountId: accountId || undefined,
      evmAddress: evmAddress as Hex,
      suiAddress,
      cardanoAddress,
      solanaAddress: solanaWallet?.publicKey?.toBase58() || undefined,
      chainId,
      localAgent: config.localAgent,
    } satisfies ChatRequestBody,
  });

  // Update the chat state atom only when API URL is configured
  useEffect(() => {
    if (absoluteApiUrl) {
      setChatState(chatState);
    } else {
      // Clear chat state if API URL is not configured
      setChatState(null);
    }
  }, [chatState, setChatState, absoluteApiUrl]);

  return <>{children}</>;
}

// Helper to update chat config
export function useChatConfig(): (config: Partial<ChatConfig>) => void {
  const setChatConfig = useSetAtom(chatConfigAtom);

  return (config: Partial<ChatConfig>) => {
    setChatConfig((prev: ChatConfig) => ({
      ...prev,
      ...config,
    }));
  };
}
